import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Button from 'components/atoms/Button';
import AutoComplete from 'components/molecules/AutoComplete';
import { EmptyStateImageSrc, EmptyStateTasks } from 'components/partials/EmptyState/utils';
import { PageHeader, PageWrapper } from 'components/partials';
import Spinner from 'components/widgets/Spinner';
import { getReadOnly } from 'selectors/permissions';
import { getAdminModeEnabled, getUserData } from 'selectors/user';
import ClearSearchIcon from '../resources/svg/small_clear_field.svg';
import {
    ANDROID_PLATFORM,
    DEFAULT_SITE_ICON,
    DEFAULT_PLATFORM,
    DEV_STATUS_IN_PRODUCTION,
    DEV_STATUS_IN_TESTING,
    DEVELOPMENT_STATUS_VALUES,
    IOS_PLATFORM,
    NEW_APP_ID,
    SINGULAR_DEFAULT_SITE_ICON,
} from './constants';
import * as service from './service';
import {
    filterApps,
    getAvailableDevelopmentStatuses,
    getCanonicalPlatformNames,
    getPlatformDisplayName,
    getPlatformFamilyName,
    getPlatformName,
    getStatusLabel,
    buildSiteSavePayload,
    normalizeSite,
    normalizeApps,
    translateText,
    validateSiteForSave,
} from './utils';
import AppCard from './components/AppCard';
import { getDraftPlatformSelection, STORE_SELECTION_PLATFORMS } from './components/appCardUtils';
import { SwitchLine } from './components/controls';
import { ConfirmDialog, FirstAppDialog } from './components/dialogs';
import { trackAppsEvent, trackAppsRefresh, trackSessionDedupWindowSave } from './mixpanel';
import css from './Apps.css';

const LIVE_VALIDATION_FIELDS = ['url', 'longname', 'public_id', 'platform', 'os', '_published', 'session_dedup_window', 'storeDetectedPlatformName'];
const NEW_APP_QUERY_PARAM = 'new-app';
const APPS_EMPTY_STATE_HEADER_DATA = {
    titleText: 'STATIC.EMPTY_STATE.TITLE',
    subTitleText: 'STATIC.PAGES.APPS_CONFIGURATION.EMPTY_STATE_TITLE',
    tasks: [
        {
            topic: EmptyStateTasks.ADD_APP,
            name: 'STATIC.EMPTY_STATE.TASKS.ADD_APP_TASK',
        },
        {
            topic: EmptyStateTasks.SDK_INTEGRATED,
            name: 'STATIC.EMPTY_STATE.TASKS.SDK_TASK',
            goRedirect: '#/react/sdk_integration',
        },
        {
            topic: EmptyStateTasks.ADDED_DEVICE,
            name: 'STATIC.EMPTY_STATE.TASKS.TEST_SDK_INTEGRATION',
            goRedirect: '#/react/testing-console',
        },
    ],
    imageSrc: EmptyStateImageSrc.APPS_CONFIGURATION,
};

const getAppsEmptyStateHeaderData = readOnly => ({
    ...APPS_EMPTY_STATE_HEADER_DATA,
    tasks: APPS_EMPTY_STATE_HEADER_DATA.tasks.map(task =>
        task.topic === EmptyStateTasks.ADD_APP ? { ...task, disabled: readOnly } : task
    ),
});

const createDraftSiteKey = (appId, platform) => `${appId}-${platform}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const DUPLICATE_SITE_ERROR_SUFFIX = 'sites under same org with same public_id/platform combo';
const DUPLICATE_LONGNAME_ERROR_CODE = 'ERROR_DUPLICATE_LONGNAME';
const ATTRIBUTION_WINDOW_FIELDS = ['inactivity_window_h', 'session_dedup_window'];
const KNOWN_API_ERROR_CODES = ['ERROR_INVALID_PERMISSIONS', 'ERROR_ADMIN_MODE_ADMON_CONFIG'];
const BACKEND_FIELD_ERROR_CODES = {
    ERROR_INVALID_P12_DATA: {
        field: '_local_ios_push_certificate_file',
        message: 'Invalid certificate file',
    },
    ERROR_INVALID_P12_PASSWORD: {
        field: 'ios_push_certificate_p12_key',
        message: 'Invalid certificate password',
    },
    ERROR_INVALID_FCM_SERVER_KEY: {
        field: 'fcm_server_key',
        message: 'Invalid FCM server key',
    },
    ERROR_INVALID_FCM_PROJECT_ID: {
        field: 'fcm_project_id',
        message: 'Invalid FCM project ID',
    },
    ERROR_IOS_UNIVERSAL_LINK_DOMAIN_PREFIX_TAKEN: {
        field: 'ios_universal_link_subdomain',
        message: 'Domain name prefix is already taken',
    },
};
const KNOWN_API_ERRORS = {
    'A site with the same public_id already exists!': 'This identifier is currently in use by another app. Please use another identifier.',
    'Keyword is already in use': 'This mapping key is already in use by another app. Please use another value.',
};

const getErrorText = error => {
    if (typeof error === 'string') {
        return error;
    }

    return error?.value || error?.text || error?.data?.value || error?.error || error?.message || '';
};

const getDuplicateLongnameDetails = (error, apps = []) => {
    const errorData = error?.data?.extra || error?.extra;

    if (errorData?.error_code !== DUPLICATE_LONGNAME_ERROR_CODE) {
        return null;
    }

    const duplicatingSiteInfo = errorData.duplicating_site_info || {};
    const duplicatingApp = apps.find(app => String(app.id) === String(duplicatingSiteInfo.app_id));

    return {
        appId: duplicatingSiteInfo.app_id,
        appName: duplicatingApp ? duplicatingApp.formName || duplicatingApp.name : null,
        developmentStatus: duplicatingSiteInfo.development_status,
        longname: duplicatingSiteInfo.longname || '',
        siteId: duplicatingSiteInfo.site_id,
    };
};

const getSaveErrorMessage = (error, translate) => {
    const errorData = error?.data?.extra || error?.extra;
    const errorText = getErrorText(error);

    if (!errorData && KNOWN_API_ERRORS[errorText]) {
        return KNOWN_API_ERRORS[errorText];
    }

    if (!errorData && errorText.endsWith(DUPLICATE_SITE_ERROR_SUFFIX)) {
        return 'Public ID is already in use';
    }

    if (errorData?.error_code === 1 && errorText) {
        return errorText;
    }

    if (KNOWN_API_ERROR_CODES.includes(errorData?.error_code) && errorData?.error_message) {
        return errorData.error_message;
    }

    if (errorData?.error_message) {
        return errorData.error_message;
    }

    if (errorText) {
        return errorText;
    }

    return translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.SAVE_ERROR');
};

const getDuplicateAppName = (apps, appId, appName) => {
    const normalizedName = appName.trim().toLowerCase();

    return apps.find(app => app.id !== appId && (app.name || '').trim().toLowerCase() === normalizedName);
};

const translateWithFallback = (translate, id, fallback, data = null) => {
    const translated = translateText(translate, id, data);

    return translated === id ? fallback : translated;
};

const getDuplicateAppNameMessage = (translate, appName) =>
    translateWithFallback(
        translate,
        'STATIC.PAGES.APPS_CONFIGURATION.DUPLICATE_APP_NAME',
        `An app called '${appName}' already exists! please change the name or edit the existing app.`,
        { appName }
    );

const mappingKeyChanged = (committedSite, site) => {
    const originalKeyword = committedSite?.keyword || '';
    const nextKeyword = site.keyword || '';

    return !!originalKeyword && originalKeyword !== nextKeyword;
};

const mapAppById = (apps, appId, mapApp) => apps.map(app => (app.id === appId ? mapApp(app) : app));

const mapSiteByKey = (apps, appId, siteKey, getSiteKeyFn, mapSite) =>
    apps.map(app =>
        app.id === appId
            ? {
                  ...app,
                  sites: app.sites.map(site => (getSiteKeyFn(appId, site) === siteKey ? mapSite(site) : site)),
              }
            : app
    );

const removeSiteByKey = (apps, appId, siteKey, getSiteKeyFn) =>
    apps.map(app =>
        app.id === appId
            ? {
                  ...app,
                  sites: app.sites.filter(site => getSiteKeyFn(appId, site) !== siteKey),
              }
            : app
    );

const hasNewAppUrlParam = () => {
    const hashSearch = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';

    return new URLSearchParams(window.location.search).has(NEW_APP_QUERY_PARAM) || new URLSearchParams(hashSearch).has(NEW_APP_QUERY_PARAM);
};

const removeNewAppUrlParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(NEW_APP_QUERY_PARAM);

    if (url.hash.includes('?')) {
        const [hashPath, hashSearch] = url.hash.split('?');
        const hashParams = new URLSearchParams(hashSearch);
        hashParams.delete(NEW_APP_QUERY_PARAM);
        const nextHashSearch = hashParams.toString();
        url.hash = nextHashSearch ? `${hashPath}?${nextHashSearch}` : hashPath;
    }

    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};

const getBackendFieldValidationError = error => {
    const errorData = error?.data?.extra || error?.extra;
    const fieldError = BACKEND_FIELD_ERROR_CODES[errorData?.error_code];

    if (!fieldError) {
        return null;
    }

    return {
        [fieldError.field]: errorData?.error_message || fieldError.message,
    };
};

const mergeSavedSiteState = (site, savedSite) => {
    const mergedSite = { ...site, ...savedSite };
    const futureAdMonSource =
        typeof site.new_admon_revenue_source === 'object'
            ? site.new_admon_revenue_source
            : { name: site.new_admon_revenue_source, display_name: site.new_admon_revenue_source };
    const futureAdMonSourceName = futureAdMonSource?.name;

    ATTRIBUTION_WINDOW_FIELDS.forEach(field => {
        if ((savedSite?.[field] === null || savedSite?.[field] === undefined) && site[field] !== undefined) {
            mergedSite[field] = site[field];
        }
    });

    mergedSite.initial_inactivity_window = mergedSite.inactivity_window_h;
    mergedSite.initial_session_dedup_window = mergedSite.session_dedup_window;

    if (futureAdMonSourceName) {
        mergedSite.admon_revenue_source = {
            ...futureAdMonSource,
        };
        mergedSite.admon_revenue_upcoming_source = {
            admon_source: futureAdMonSource.display_name || futureAdMonSourceName,
            start_date: site.new_admon_revenue_start_date,
        };
        mergedSite.admon_revenue_source_read_only = true;
        mergedSite.new_admon_revenue_source = '';
        mergedSite.new_admon_revenue_start_date = '';
    }

    mergedSite.new_admon_dialog_open = false;
    mergedSite.admon_changed = false;

    return mergedSite;
};

const readFileAsDataUrl = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result), false);
        reader.addEventListener('error', reject, false);
        reader.readAsDataURL(file);
    });

const prepareSiteSavePayload = site => {
    const payload = buildSiteSavePayload(site);
    const localCertificateFile = site._local_ios_push_certificate_file;

    delete payload._local_ios_push_certificate_file;

    if (typeof localCertificateFile === 'undefined') {
        return Promise.resolve({
            ...payload,
            ios_push_certificate_p12_data: null,
            ios_push_certificate_p12_filename: null,
        });
    }

    if (localCertificateFile.fromServer) {
        return Promise.resolve({
            ...payload,
            ios_push_certificate_p12_data: null,
            ios_push_certificate_p12_filename: localCertificateFile.name,
        });
    }

    return readFileAsDataUrl(localCertificateFile).then(dataUrl => ({
        ...payload,
        ios_push_certificate_p12_data: dataUrl,
        ios_push_certificate_p12_filename: localCertificateFile.name,
    }));
};

const stableStringify = value => {
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`;
    }

    if (value && typeof value === 'object') {
        return `{${Object.keys(value)
            .sort()
            .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
            .join(',')}}`;
    }

    return JSON.stringify(value);
};

const getSiteDirtyKey = site => stableStringify(buildSiteSavePayload(site));

function AppsPage({ translate = id => id }) {
    const adminMode = useSelector(getAdminModeEnabled);
    const readOnly = useSelector(getReadOnly);
    const userData = useSelector(getUserData);
    const emptyStateOrgData = useSelector(state => state?.onboarding?.data?.empty_state_data || null);

    const [apps, setApps] = useState([]);
    const [committedApps, setCommittedApps] = useState([]);
    const [configuration, setConfiguration] = useState({});
    const [appsPermissions, setAppsPermissions] = useState({
        appKeywordEnabled: false,
        fraudPermitted: false,
        userPrivacyEnabled: false,
    });
    const [platforms, setPlatforms] = useState([]);
    const [isStandardTier, setIsStandardTier] = useState(true);
    const [expandedSites, setExpandedSites] = useState({});
    const [searchText, setSearchText] = useState('');
    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [developmentStatus, setDevelopmentStatus] = useState(DEV_STATUS_IN_PRODUCTION);
    const [showStoreLiveOnly, setShowStoreLiveOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [refreshError, setRefreshError] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [firstAppPrompt, setFirstAppPrompt] = useState(null);
    const handledNewAppParamRef = useRef(false);
    const saveSiteRef = useRef(null);

    const loadApps = useCallback(({ syncApsalarApps = false } = {}) => {
        if (syncApsalarApps) {
            setRefreshing(true);
            setRefreshError(false);
            trackAppsRefresh();
        } else {
            setLoading(true);
            setError(false);
        }

        return Promise.all([
            service.getConfiguration(),
            service.getPlatforms(),
            service.getApps({
                syncApsalarApps,
                developmentStatusesWhitelist: DEVELOPMENT_STATUS_VALUES,
            }),
        ])
            .then(([configurationResponse, platformsResponse, appsResponse]) => {
                setConfiguration(configurationResponse.configuration || {});
                const nextPlatforms = platformsResponse.platforms || [];
                setPlatforms(nextPlatforms);
                setIsStandardTier(!!appsResponse.is_standard_tier);
                setAppsPermissions({
                    appKeywordEnabled: !!appsResponse.app_keyword_enabled,
                    fraudPermitted: !!appsResponse.fraud_permitted,
                    userPrivacyEnabled: !!appsResponse.user_privacy_enabled,
                });
                const normalizedApps = normalizeApps(appsResponse.apps || [], nextPlatforms);
                setApps(normalizedApps);
                setCommittedApps(normalizedApps);
            })
            .catch(() => {
                if (syncApsalarApps) {
                    setRefreshError(true);
                } else {
                    setError(true);
                }
            })
            .finally(() => {
                setLoading(false);
                setRefreshing(false);
            });
    }, []);

    useEffect(() => {
        loadApps();
    }, [loadApps]);

    const appCountByStatus = useMemo(
        () =>
            DEVELOPMENT_STATUS_VALUES.reduce(
                (counts, status) => ({
                    ...counts,
                    [status]: filterApps({
                        apps,
                        searchText,
                        developmentStatus: status,
                        showStoreLiveOnly,
                    }).length,
                }),
                {}
            ),
        [apps, searchText, showStoreLiveOnly]
    );

    const isSiteDirty = useCallback(
        (appId, site) => {
            if (!site.id) return true;

            const committedApp = committedApps.find(app => app.id === appId);
            const committedSite = committedApp?.sites?.find(currSite => currSite.id === site.id);

            if (!committedSite) return true;

            return getSiteDirtyKey(site) !== getSiteDirtyKey(committedSite);
        },
        [committedApps]
    );
    const availableDevelopmentStatuses = useMemo(() => getAvailableDevelopmentStatuses(apps), [apps]);
    const availablePlatformNames = useMemo(() => getCanonicalPlatformNames(platforms), [platforms]);
    const appNameSuggestions = useMemo(
        () =>
            [...new Set(apps.map(app => (app.formName || app.name || '').trim()).filter(Boolean))].sort((left, right) =>
                left.localeCompare(right)
            ),
        [apps]
    );
    const matchingAppNameSuggestions = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();

        return appNameSuggestions.filter(appName => !normalizedSearch || appName.toLowerCase().includes(normalizedSearch));
    }, [appNameSuggestions, searchText]);
    const visibleApps = useMemo(
        () =>
            filterApps({
                apps,
                searchText,
                developmentStatus,
                showStoreLiveOnly,
            }),
        [apps, developmentStatus, searchText, showStoreLiveOnly]
    );

    const showTabs = !isStandardTier;
    const showLiveOnlyToggle = showStoreLiveOnly || availableDevelopmentStatuses[developmentStatus];
    const isUnifiedUser = !!userData?.is_unified || !!configuration.apsalar_enabled;
    const defaultPlatformName = availablePlatformNames.includes(DEFAULT_PLATFORM)
        ? DEFAULT_PLATFORM
        : availablePlatformNames[0] || DEFAULT_PLATFORM;
    const showAppsEmptyState = !!emptyStateOrgData?.show_apps_empty_state && apps.length === 0;
    const appsEmptyStateHeaderData = useMemo(() => getAppsEmptyStateHeaderData(readOnly), [readOnly]);
    const searchOptions = useMemo(
        () => matchingAppNameSuggestions.map(appName => ({ label: appName, value: appName })),
        [matchingAppNameSuggestions]
    );

    const handleSearchChange = useCallback(value => {
        setSelectedSearchOption(null);
        setSearchText(value);
    }, []);

    const clearSearch = useCallback(() => {
        setSelectedSearchOption(null);
        setSearchText('');
    }, []);

    const applySearchSuggestion = useCallback(selectedOption => {
        setSelectedSearchOption(selectedOption || null);
        setSearchText(selectedOption?.value || '');
    }, []);

    const getSiteKey = useCallback(
        (appId, site) => site.id || site._clientKey || `${appId}-${getPlatformName(site)}-${site.url || site.longname || 'new'}`,
        []
    );

    const toggleSite = useCallback(
        (appId, site) => {
            const key = getSiteKey(appId, site);
            setExpandedSites(prev => ({ ...prev, [key]: !prev[key] }));
        },
        [getSiteKey]
    );

    const patchSite = useCallback(
        (appId, site, patch) => {
            const siteKey = getSiteKey(appId, site);
            const shouldClearValidation =
                !Object.prototype.hasOwnProperty.call(patch, 'validationErrors') &&
                !Object.prototype.hasOwnProperty.call(patch, 'saveError') &&
                !Object.prototype.hasOwnProperty.call(patch, 'longnameDuplicationDetails') &&
                !Object.prototype.hasOwnProperty.call(patch, 'isSaving') &&
                !Object.prototype.hasOwnProperty.call(patch, 'isLoadingStoreDetails') &&
                !Object.prototype.hasOwnProperty.call(patch, 'isTestingFcmIntegration') &&
                !Object.prototype.hasOwnProperty.call(patch, 'fcmIntegrationMessage');
            const nextPatch = shouldClearValidation
                ? { ...patch, validationErrors: null, saveError: null, longnameDuplicationDetails: null }
                : patch;
            setApps(prevApps =>
                mapSiteByKey(prevApps, appId, siteKey, getSiteKey, currSite => {
                    const nextSite = { ...currSite, ...nextPatch };
                    const shouldRevalidate =
                        Object.keys(patch).some(field => LIVE_VALIDATION_FIELDS.includes(field)) ||
                        !!currSite.validationErrors;

                    if (!shouldRevalidate) {
                        return nextSite;
                    }

                    const validationErrors = validateSiteForSave({
                        apps: prevApps,
                        site: nextSite,
                        adminMode,
                        userData,
                        showRequired: false,
                    });

                    return {
                        ...nextSite,
                        validationErrors: Object.keys(validationErrors).length ? validationErrors : null,
                    };
                })
            );
        },
        [adminMode, getSiteKey, userData]
    );

    const confirmAction = useCallback(
        ({
            titleKey,
            titleFallback,
            messageKey,
            messageFallback,
            confirmLabel,
            confirmLabelKey,
            confirmLabelFallback,
            onConfirm,
        }) => {
            setConfirmation({
                title: titleFallback
                    ? translateWithFallback(translate, titleKey, titleFallback)
                    : translateText(translate, titleKey),
                message: messageFallback
                    ? translateWithFallback(translate, messageKey, messageFallback)
                    : translateText(translate, messageKey),
                confirmLabel:
                    confirmLabel ||
                    (confirmLabelFallback
                        ? translateWithFallback(translate, confirmLabelKey, confirmLabelFallback)
                        : translateText(translate, confirmLabelKey)),
                onConfirm,
            });
        },
        [translate]
    );

    const yesConfirmLabel = useCallback(
        actionKey =>
            `${translateWithFallback(translate, 'STATIC.BUTTONS.YES', 'Yes')}, ${translateText(
                translate,
                actionKey
            ).toLowerCase()}`,
        [translate]
    );

    const patchSiteWithConfirmation = useCallback(
        (appId, site, patch) => {
            const isPurchaseEnforcementOn = !!site.iap_enforcement_enabled && !!site.iap_enforcement_date;

            if (patch.iap_enforcement_enabled === true && !isPurchaseEnforcementOn) {
                confirmAction({
                    titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.ANDROID_PURCHASE_ENFORCEMENT_DATE_WARNING_HEADER',
                    messageKey: 'STATIC.PAGES.APPS_CONFIGURATION.ANDROID_PURCHASE_ENFORCEMENT_DATE_WARNING',
                    confirmLabelKey: 'STATIC.BUTTONS.CONTINUE',
                    onConfirm: () =>
                        patchSite(appId, site, {
                            ...patch,
                            iap_enforcement_date: new Date().toISOString(),
                        }),
                });
                return;
            }

            if (patch.iap_enforcement_enabled === false) {
                patchSite(appId, site, {
                    ...patch,
                    iap_enforcement_date: null,
                });
                return;
            }

            patchSite(appId, site, patch);
        },
        [confirmAction, patchSite]
    );

    const setSiteSavingState = useCallback(
        (appId, site, patch) => {
            const siteKey = getSiteKey(appId, site);
            setApps(prevApps =>
                mapSiteByKey(prevApps, appId, siteKey, getSiteKey, currSite => ({ ...currSite, ...patch }))
            );
        },
        [getSiteKey]
    );

    const refreshSiteStoreDetails = useCallback(
        (app, site, storeUrlOverride = null) => {
            const storeUrl = (storeUrlOverride ?? site.url ?? '').trim();
            const siteKey = getSiteKey(app.id, site);

            if (!storeUrl) {
                patchSite(app.id, site, { icon: DEFAULT_SITE_ICON });
                return Promise.resolve();
            }

            patchSite(app.id, site, { isLoadingStoreDetails: true, saveError: null });

            return service
                .getStoreDetails(storeUrl, siteKey)
                .then(response => {
                    const details = response?.data?.value || response || {};
                    const platformOptions = details.platforms || [];
                    const firstPlatform = platformOptions[0];
                    const firstPlatformName = firstPlatform ? getPlatformName(firstPlatform) : null;
                    const selectedPlatform = firstPlatformName
                        ? platforms.find(platform => platform.name === firstPlatformName) || {
                              name: firstPlatformName,
                              display_name: getPlatformDisplayName(firstPlatformName),
                          }
                        : null;

                    setApps(prevApps => {
                        const nextApps = prevApps.map(currApp => {
                            if (currApp.id !== app.id) return currApp;

                            const appName = (currApp.formName || currApp.name || '').trim();
                            const shouldSetAppName = currApp.id === NEW_APP_ID && !appName && details.name;
                            const shouldSetAppIcon =
                                !!details.icon_url &&
                                (!currApp.icon ||
                                    currApp.icon === DEFAULT_SITE_ICON ||
                                    currApp.icon === SINGULAR_DEFAULT_SITE_ICON);

                            return {
                                ...currApp,
                                icon: shouldSetAppIcon ? details.icon_url : currApp.icon,
                                name: shouldSetAppName ? details.name : currApp.name,
                                formName: shouldSetAppName ? details.name : currApp.formName,
                            };
                        });

                        return mapSiteByKey(nextApps, app.id, siteKey, getSiteKey, currSite => {
                            const sitePatch = {
                                isLoadingStoreDetails: false,
                                saveError: null,
                                validationErrors: null,
                            };

                            if (details.icon_url) sitePatch.icon = details.icon_url;
                            if (details.public_id !== undefined) sitePatch.public_id = details.public_id;
                            if (details.is_live_in_store !== undefined) sitePatch.is_live_in_store = details.is_live_in_store;
                            if (firstPlatformName) sitePatch.storeDetectedPlatformName = firstPlatformName;
                            if (!currSite.longname && details.bundle_id) sitePatch.longname = details.bundle_id;
                            if ((app.id === NEW_APP_ID || !currSite.id) && selectedPlatform) {
                                sitePatch.platform = selectedPlatform;
                                sitePatch.os = selectedPlatform;
                            }

                            return { ...currSite, ...sitePatch };
                        });
                    });
                })
                .catch(() => {
                    patchSite(app.id, site, { isLoadingStoreDetails: false });
                });
        },
        [getSiteKey, patchSite, platforms]
    );

    const changeAppName = useCallback((appId, name) => {
        setApps(prevApps => mapAppById(prevApps, appId, app => ({ ...app, formName: name, appNameError: null })));
    }, []);

    const saveAppName = useCallback(
        app => {
            const nextName = (app.formName || '').trim();
            const originalName = app.name || '';

            if (!nextName) {
                setApps(prevApps =>
                    mapAppById(prevApps, app.id, currApp => ({
                        ...currApp,
                        formName: app.id === NEW_APP_ID ? '' : originalName,
                    }))
                );
                return Promise.resolve();
            }

            if (getDuplicateAppName(apps, app.id, nextName)) {
                window.alert(getDuplicateAppNameMessage(translate, nextName));
                setApps(prevApps =>
                    mapAppById(prevApps, app.id, currApp => ({
                        ...currApp,
                        formName: app.id === NEW_APP_ID ? '' : originalName,
                    }))
                );
                return Promise.resolve();
            }

            if (app.id === NEW_APP_ID || nextName === app.name) return Promise.resolve();

            return service.saveApp(app.id, nextName).then(() => {
                setApps(prevApps =>
                    mapAppById(prevApps, app.id, currApp => ({ ...currApp, name: nextName, formName: nextName }))
                );
                trackAppsEvent('app name edited', { app_id: app.id, app_name: nextName });
            });
        },
        [apps, translate]
    );

    const closeConfirmation = useCallback(() => setConfirmation(null), []);

    const acceptConfirmation = useCallback(() => {
        const action = confirmation?.onConfirm;
        setConfirmation(null);
        return action ? action() : null;
    }, [confirmation]);

    const deleteApp = useCallback(
        app => {
            confirmAction({
                titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_DELETE_APP',
                messageKey: 'STATIC.PAGES.DELETE_MODAL.WARNING_P2',
                confirmLabel: yesConfirmLabel('STATIC.PAGES.APPS_CONFIGURATION.DELETE_APP'),
                onConfirm: () => {
                    if (app.id === NEW_APP_ID) {
                        setApps(prevApps => prevApps.filter(currApp => currApp.id !== NEW_APP_ID));
                        return Promise.resolve();
                    }
                    return service.deleteApp(app.id).then(() => {
                        setApps(prevApps => prevApps.filter(currApp => currApp.id !== app.id));
                        trackAppsEvent('app deleted', { app_id: app.id, app_name: app.name });
                    });
                },
            });
        },
        [confirmAction, yesConfirmLabel]
    );

    const deleteSite = useCallback(
        (appId, site) => {
            const siteKey = getSiteKey(appId, site);

            confirmAction({
                titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_DELETE_SITE',
                messageKey: 'STATIC.PAGES.DELETE_MODAL.WARNING_P2',
                confirmLabel: yesConfirmLabel('STATIC.PAGES.APPS_CONFIGURATION.DELETE_SITE'),
                onConfirm: () => {
                    if (!site.id) {
                        setApps(prevApps =>
                            removeSiteByKey(prevApps, appId, siteKey, getSiteKey).filter(
                                app => app.id !== NEW_APP_ID || app.sites.length
                            )
                        );
                        return Promise.resolve();
                    }
                    return service.deleteSite(site.id).then(() => {
                        const appName = apps.find(app => app.id === appId)?.name || '';
                        setApps(prevApps =>
                            removeSiteByKey(prevApps, appId, siteKey, getSiteKey).filter(app => app.sites.length)
                        );
                        trackAppsEvent('site deleted', { app_id: appId, app_name: appName, site_id: site.id });
                    });
                },
            });
        },
        [apps, confirmAction, getSiteKey, yesConfirmLabel]
    );

    const cancelSite = useCallback(
        (appId, site) => {
            const key = getSiteKey(appId, site);
            if (!site.id) {
                setApps(prevApps =>
                    removeSiteByKey(prevApps, appId, key, getSiteKey).filter(
                        app => app.id !== NEW_APP_ID || app.sites.length
                    )
                );
            }
            const committedApp = committedApps.find(currApp => currApp.id === appId);
            const committedSite = committedApp?.sites?.find(currSite => currSite.id === site.id);

            if (committedSite) {
                setApps(prevApps =>
                    mapSiteByKey(prevApps, appId, key, getSiteKey, () => ({
                        ...committedSite,
                        validationErrors: null,
                        saveError: null,
                        longnameDuplicationDetails: null,
                        isSaving: false,
                    }))
                );
            }
            setExpandedSites(prev => ({ ...prev, [key]: false }));
        },
        [committedApps, getSiteKey]
    );

    const toggleSiteDevelopmentStatus = useCallback(
        (appId, site, nextStatus) => {
            if (isStandardTier) return Promise.resolve();
            if (!site.id) return Promise.resolve();

            const siteKey = getSiteKey(appId, site);
            const actionKey =
                nextStatus === DEV_STATUS_IN_PRODUCTION
                    ? 'STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_LIVE'
                    : 'STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_TEST';

            confirmAction({
                titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_MOVE_SITE_STATUS',
                messageKey: 'STATIC.PAGES.VALIDATE_ACTION_MODAL.WARNING_P2',
                confirmLabel: yesConfirmLabel(actionKey),
                onConfirm: () =>
                    service.setSiteDevelopmentStatus(site.id, nextStatus).then(() => {
                        patchSite(appId, site, { development_status: nextStatus });
                        setCommittedApps(prevApps =>
                            mapSiteByKey(prevApps, appId, siteKey, getSiteKey, currSite => ({
                                ...currSite,
                                development_status: nextStatus,
                            }))
                        );
                        trackAppsEvent('status changed', {
                            site_id: site.id,
                            status: site.development_status,
                            new_status: nextStatus,
                        });
                    }),
            });

            return null;
        },
        [confirmAction, getSiteKey, isStandardTier, patchSite, yesConfirmLabel]
    );

    const moveSite = useCallback(
        (sourceAppId, site, destinationAppId) => {
            if (!site.id || !destinationAppId || sourceAppId === destinationAppId) return Promise.resolve();
            const siteKey = getSiteKey(sourceAppId, site);
            const moveSiteBetweenApps = prevApps => {
                const siteToMove = prevApps
                    .find(app => app.id === sourceAppId)
                    ?.sites.find(currSite => getSiteKey(sourceAppId, currSite) === siteKey);

                if (!siteToMove) return prevApps;

                return removeSiteByKey(prevApps, sourceAppId, siteKey, getSiteKey)
                    .map(app => {
                        if (app.id === destinationAppId) {
                            return {
                                ...app,
                                sites: [...app.sites, siteToMove],
                            };
                        }
                        return app;
                    })
                    .filter(app => app.sites.length);
            };

            confirmAction({
                titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_MOVE_SITE_APP',
                messageKey: 'STATIC.PAGES.VALIDATE_ACTION_MODAL.WARNING_P2',
                confirmLabel: yesConfirmLabel('STATIC.PAGES.APPS_CONFIGURATION.MOVE_TO_ANOTHER_APP'),
                onConfirm: () =>
                    service.moveSite(site.id, destinationAppId).then(() => {
                        const sourceApp = apps.find(app => app.id === sourceAppId);
                        const destinationApp = apps.find(app => app.id === destinationAppId);
                        setApps(moveSiteBetweenApps);
                        setCommittedApps(moveSiteBetweenApps);
                        setExpandedSites(prev => {
                            const nextExpandedSites = { ...prev };
                            delete nextExpandedSites[siteKey];
                            nextExpandedSites[getSiteKey(destinationAppId, site)] = true;
                            return nextExpandedSites;
                        });
                        trackAppsEvent('moved site', {
                            app_id: sourceAppId,
                            app_name: sourceApp?.name || '',
                            new_app_id: destinationAppId,
                            new_app_name: destinationApp?.name || '',
                            site_id: site.id,
                        });
                    }),
            });

            return null;
        },
        [apps, confirmAction, getSiteKey, yesConfirmLabel]
    );

    const testFcmIntegration = useCallback(
        (appId, site) => {
            if (!site.fcm_project_id) return Promise.resolve();

            patchSite(appId, site, { isTestingFcmIntegration: true, fcmIntegrationMessage: null });

            return service
                .testFcmIntegration(site.fcm_project_id)
                .then(response => {
                    const integrationSucceeded = response?.status === true || response?.value?.status === true;

                    patchSite(appId, site, {
                        isTestingFcmIntegration: false,
                        fcmIntegrationStatus: integrationSucceeded,
                        fcmIntegrationMessage: integrationSucceeded
                            ? 'Connection OK'
                            : 'Connection failed. Please ensure Project ID is configured properly.',
                    });
                })
                .catch(() => {
                    patchSite(appId, site, {
                        isTestingFcmIntegration: false,
                        fcmIntegrationStatus: false,
                        fcmIntegrationMessage: 'Connection failed. Please ensure Project ID is configured properly.',
                    });
                });
        },
        [patchSite]
    );

    const replaceNewAppId = useCallback((previousId, nextId) => {
        setApps(prevApps =>
            mapAppById(prevApps, previousId, app => ({
                ...app,
                id: nextId,
                name: app.formName,
                formName: app.formName,
            }))
        );
    }, []);

    const requestMappingKeyChangeConfirmation = useCallback(
        (app, site) => {
            confirmAction({
                titleKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_EDIT_MAPPING_KEY',
                titleFallback: 'Are you sure you want to make these changes?',
                messageKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_EDIT_MAPPING_KEY_WARNING',
                messageFallback: 'Changing your appsite keyword will affect your current campaign data.',
                confirmLabelKey: 'STATIC.PAGES.APPS_CONFIGURATION.CONFIRM_EDIT_MAPPING_KEY_ACTION',
                confirmLabelFallback: 'Yes, edit keyword',
                onConfirm: () => saveSiteRef.current?.(app, site, { skipMappingKeyConfirmation: true }),
            });
        },
        [confirmAction]
    );

    const saveSite = useCallback(
        async (app, site, { skipMappingKeyConfirmation = false } = {}) => {
            const appName = (app.formName || app.name || '').trim();
            if (!appName) {
                setApps(prevApps =>
                    mapAppById(prevApps, app.id, currApp => ({ ...currApp, appNameError: 'Field cannot be empty' }))
                );
                return;
            }

            const siteKey = getSiteKey(app.id, site);
            const isDraftSite = !site.id;
            if (getDuplicateAppName(apps, app.id, appName)) {
                window.alert(getDuplicateAppNameMessage(translate, appName));
                return;
            }

            const committedApp = committedApps.find(currApp => currApp.id === app.id);
            const committedSite = committedApp?.sites?.find(currSite => currSite.id === site.id);

            if (!skipMappingKeyConfirmation && mappingKeyChanged(committedSite, site)) {
                requestMappingKeyChangeConfirmation(app, site);
                return;
            }

            const validationErrors = validateSiteForSave({
                apps,
                site,
                adminMode,
                userData,
            });
            if (Object.keys(validationErrors).length) {
                setSiteSavingState(app.id, site, {
                    validationErrors,
                    saveError: Object.values(validationErrors).join(', '),
                    longnameDuplicationDetails: null,
                });
                return;
            }

            let activeAppId = app.id;
            const isFirstAppSave =
                app.id === NEW_APP_ID && apps.filter(currApp => currApp.id !== NEW_APP_ID).length === 0;
            setSiteSavingState(app.id, site, {
                isSaving: true,
                saveError: null,
                validationErrors: null,
                longnameDuplicationDetails: null,
            });

            try {
                activeAppId = app.id === NEW_APP_ID ? (await service.saveNewApp(appName)).id : app.id;

                if (app.id === NEW_APP_ID) {
                    replaceNewAppId(app.id, activeAppId);
                }

                const sitePayload = await prepareSiteSavePayload(site);
                const savedSite = await service.saveSite(activeAppId, sitePayload, siteKey);
                const mergedSavedSite = mergeSavedSiteState(site, savedSite);
                const savedSiteState = normalizeSite({ ...mergedSavedSite, isSaving: false }, platforms);

                setApps(prevApps =>
                    prevApps.map(currApp => {
                        if (currApp.id !== activeAppId && currApp.id !== app.id) return currApp;
                        return {
                            ...currApp,
                            id: activeAppId,
                            name: appName,
                            formName: appName,
                            sites: currApp.sites.map(currSite =>
                                getSiteKey(app.id, currSite) === siteKey ? { ...currSite, ...savedSiteState } : currSite
                            ),
                        };
                    })
                );
                setCommittedApps(prevApps => {
                    const nextApps = prevApps.map(currApp => {
                        if (currApp.id !== activeAppId && currApp.id !== app.id) return currApp;

                        const existingSiteIndex = currApp.sites.findIndex(
                            currSite => currSite.id === savedSiteState.id
                        );
                        const nextSites =
                            existingSiteIndex === -1
                                ? [...currApp.sites, savedSiteState]
                                : currApp.sites.map(currSite =>
                                      currSite.id === savedSiteState.id ? savedSiteState : currSite
                                  );

                        return {
                            ...currApp,
                            id: activeAppId,
                            name: appName,
                            formName: appName,
                            sites: nextSites,
                        };
                    });

                    if (nextApps.some(currApp => currApp.id === activeAppId)) {
                        return nextApps;
                    }

                    return [
                        ...nextApps,
                        {
                            ...app,
                            id: activeAppId,
                            name: appName,
                            formName: appName,
                            sites: [savedSiteState],
                        },
                    ];
                });
                if (app.id === NEW_APP_ID) {
                    trackAppsEvent('new app added', {
                        app_id: activeAppId,
                        app_name: appName,
                    });
                }
                if (isDraftSite) {
                    trackAppsEvent('new site added', {
                        app_id: activeAppId,
                        app_name: appName,
                        site_id: savedSiteState.id,
                        platform: getPlatformName(savedSiteState),
                        is_live_in_store: !!savedSiteState.is_live_in_store,
                    });
                }
                if (
                    !isDraftSite &&
                    committedSite &&
                    committedSite.session_dedup_window !== savedSiteState.session_dedup_window
                ) {
                    trackSessionDedupWindowSave({
                        org: userData?.org_name || userData?.orgName,
                        app_id: activeAppId,
                        app_name: appName,
                        site_id: savedSiteState.id,
                        platform: getPlatformName(savedSiteState),
                        previous_window: committedSite.session_dedup_window,
                        configured_window: savedSiteState.session_dedup_window,
                    });
                }
                setExpandedSites(prev => {
                    const savedSiteKey = getSiteKey(activeAppId, mergedSavedSite);
                    const nextExpandedSites = { ...prev };
                    delete nextExpandedSites[siteKey];
                    nextExpandedSites[savedSiteKey] = false;
                    return nextExpandedSites;
                });
                if (isFirstAppSave) {
                    window.dispatchEvent(new CustomEvent('firstAppAdded', {}));
                    setFirstAppPrompt({
                        appId: activeAppId,
                        firstPlatformName: getPlatformFamilyName(mergedSavedSite),
                        type: 'add-platform',
                    });
                }
            } catch (saveErrorResponse) {
                const longnameDuplicationDetails = getDuplicateLongnameDetails(saveErrorResponse, apps);
                const backendValidationErrors = getBackendFieldValidationError(saveErrorResponse);

                setSiteSavingState(activeAppId, site, {
                    isSaving: false,
                    validationErrors: backendValidationErrors,
                    saveError: longnameDuplicationDetails ? null : getSaveErrorMessage(saveErrorResponse, translate),
                    longnameDuplicationDetails,
                });
            }
        },
        [
            adminMode,
            apps,
            committedApps,
            getSiteKey,
            platforms,
            replaceNewAppId,
            requestMappingKeyChangeConfirmation,
            setSiteSavingState,
            translate,
            userData,
        ]
    );

    useEffect(() => {
        saveSiteRef.current = saveSite;
    }, [saveSite]);

    const jumpToDuplicateSite = useCallback(
        (appId, site, details) => {
            if (!details?.appName) return;

            const siteKey = getSiteKey(appId, site);
            const duplicateApp = apps.find(
                app => String(app.id) === String(details.appId) || app.name === details.appName
            );
            const duplicateSite = duplicateApp?.sites?.find(
                currSite =>
                    (!details.longname || currSite.longname === details.longname) &&
                    (!details.developmentStatus || currSite.development_status === details.developmentStatus)
            );
            const duplicateSiteKey = duplicateApp && duplicateSite ? getSiteKey(duplicateApp.id, duplicateSite) : null;

            if (!site.id) {
                setApps(prevApps =>
                    removeSiteByKey(prevApps, appId, siteKey, getSiteKey).filter(
                        app => app.id !== NEW_APP_ID || app.sites.length > 0
                    )
                );
            }

            if (details.developmentStatus) {
                setDevelopmentStatus(details.developmentStatus);
            }
            setSearchText(details.appName);
            setExpandedSites(prev => {
                const nextExpandedSites = { ...prev };
                delete nextExpandedSites[siteKey];
                if (duplicateSiteKey) {
                    nextExpandedSites[duplicateSiteKey] = true;
                }
                return nextExpandedSites;
            });
        },
        [apps, getSiteKey]
    );

    const startAddNewApp = useCallback(() => {
        if (readOnly || apps.some(app => app.id === NEW_APP_ID)) return;
        const platform = platforms.find(currPlatform => currPlatform.name === defaultPlatformName);
        const newSite = normalizeSite(
            {
                _clientKey: createDraftSiteKey(NEW_APP_ID, defaultPlatformName),
                platform,
                os: platform,
                development_status: developmentStatus,
                icon: DEFAULT_SITE_ICON,
                _selectionMode: STORE_SELECTION_PLATFORMS.includes(defaultPlatformName) ? 'storeUrl' : 'bundleId',
                _published: true,
                url: '',
                alias: '',
                keyword: '',
                longname: '',
                public_id: '',
                attribution_app_id: '',
                inactivity_window_h: null,
                session_dedup_window: null,
            },
            platforms
        );
        setApps(prevApps => [
            {
                id: NEW_APP_ID,
                name: '',
                formName: '',
                appNameError: 'Field cannot be empty',
                icon: DEFAULT_SITE_ICON,
                sites: [newSite],
            },
            ...prevApps,
        ]);
        setExpandedSites(prev => ({ ...prev, [getSiteKey(NEW_APP_ID, newSite)]: true }));
    }, [apps, defaultPlatformName, developmentStatus, getSiteKey, platforms, readOnly]);

    const addNewApp = useCallback(() => {
        startAddNewApp();
        removeNewAppUrlParam();
    }, [startAddNewApp]);

    const restoreDuplicateSite = useCallback(
        details => {
            if (!details?.siteId) return Promise.resolve();

            return service.restoreSite(details.siteId).then(restoredSite => {
                const developmentStatusLabel =
                    restoredSite?.development_status === DEV_STATUS_IN_PRODUCTION ? 'Live' : 'Test';
                const appName = restoredSite?.app_name || '';

                window.alert(
                    `Site was restored successfully! The apps page will now refresh, you can find the restored site under ${developmentStatusLabel} Apps -> ${appName}`
                );

                return loadApps();
            });
        },
        [loadApps]
    );

    const addPlatform = useCallback(
        (appId, platform = defaultPlatformName) => {
            const targetApp = apps.find(app => app.id === appId);
            if (
                !targetApp ||
                targetApp.id === NEW_APP_ID ||
                targetApp.sites.some(site => !site.id || site.isSaving || site.isLoadingStoreDetails)
            ) {
                return;
            }
            trackAppsEvent('added new platform button clicked');

            const selectedPlatform = getDraftPlatformSelection(platforms, platform);
            const newSite = {
                id: null,
                url: '',
                alias: '',
                keyword: '',
                longname: '',
                platform: selectedPlatform.platform,
                os: selectedPlatform.os,
                _clientKey: createDraftSiteKey(appId, platform),
                public_id: '',
                attribution_app_id: '',
                inactivity_window_h: null,
                session_dedup_window: null,
                destinations: { regular: [], deeplink: [], ios_universal_link: [] },
                development_status: developmentStatus,
                _published: STORE_SELECTION_PLATFORMS.includes(platform),
                show_advanced_settings: false,
            };

            setApps(prevApps =>
                prevApps.map(app => {
                    if (app.id !== appId) return app;
                    return { ...app, sites: [...app.sites, newSite] };
                })
            );
            setExpandedSites(prev => ({ ...prev, [getSiteKey(appId, newSite)]: true }));
        },
        [apps, defaultPlatformName, developmentStatus, getSiteKey, platforms]
    );

    const closeFirstAppPrompt = useCallback(() => {
        if (firstAppPrompt?.type === 'congrats') {
            trackAppsEvent('first app congrats popup', { step_taken: 'closed popup' });
        }
        setFirstAppPrompt(null);
    }, [firstAppPrompt]);

    const continueFirstAppFlow = useCallback(() => {
        trackAppsEvent('first app adding more platform popup', {
            platform_choice: 'No Other Platform to Add',
        });
        setFirstAppPrompt({ type: 'congrats' });
    }, []);

    const addFirstAppPlatform = useCallback(() => {
        const appId = firstAppPrompt?.appId;
        const nextPlatform = firstAppPrompt?.firstPlatformName === ANDROID_PLATFORM ? IOS_PLATFORM : ANDROID_PLATFORM;

        setFirstAppPrompt(null);
        if (appId) {
            trackAppsEvent('first app adding more platform popup', {
                platform_choice: 'Adding Another Platform',
            });
            addPlatform(appId, nextPlatform);
        }
    }, [addPlatform, firstAppPrompt]);

    const redirectFirstAppStep = useCallback(step => {
        setFirstAppPrompt(null);
        trackAppsEvent('first app congrats popup', { step_taken: `redirected to ${step}` });
        window.location.hash = `#/react/${step}/`;
    }, []);

    useEffect(() => {
        if (loading || handledNewAppParamRef.current || !hasNewAppUrlParam()) {
            return;
        }

        handledNewAppParamRef.current = true;
        removeNewAppUrlParam();

        if (!readOnly) {
            addNewApp();
        }
    }, [addNewApp, loading, readOnly]);

    const handleEmptyStateTaskClick = useCallback(
        taskTopic => {
            if (!readOnly && taskTopic === EmptyStateTasks.ADD_APP) {
                addNewApp();
            }
        },
        [addNewApp, readOnly]
    );

    return (
        <PageWrapper className={css.page}>
            <PageHeader
                text="STATIC.PAGE_HEADERS.APPS_CONFIGURATION"
                subText={{
                    faqLink: 'https://singular.zendesk.com/hc/en-us/articles/360025661232',
                    tourId: '180936',
                }}
                showEmptyState={showAppsEmptyState}
                showEmptyStateCloseIcon={false}
                emptyStateHeaderData={appsEmptyStateHeaderData}
                emptyStateOrgData={emptyStateOrgData}
                onTaskClick={handleEmptyStateTaskClick}
            />
            <div className={css.toolbar}>
                <div className={css.searchBox}>
                    <AutoComplete
                        ariaLabel="STATIC.PAGES.APPS_CONFIGURATION.SEARCH_PLACEHOLDER"
                        placeholder="STATIC.PAGES.APPS_CONFIGURATION.SEARCH_PLACEHOLDER"
                        defaultValue=""
                        searchable
                        onChange={applySearchSuggestion}
                        onInputChange={handleSearchChange}
                        containerClass={css.search}
                        selectOptions={{
                            options: searchOptions,
                            disabled: loading,
                            inputValue: selectedSearchOption ? '' : searchText,
                            isSearchable: true,
                            menuPlacement: 'bottom',
                            value: selectedSearchOption,
                        }}
                    />
                    {searchText ? (
                        <button
                            type="button"
                            className={css.searchClear}
                            aria-label={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.CLEAR_SEARCH')}
                            onClick={clearSearch}
                        >
                            <ClearSearchIcon />
                        </button>
                    ) : null}
                </div>
                <div className={css.actions}>
                    {isUnifiedUser ? (
                        <Button
                            type="secondary"
                            disabled={loading}
                            showSpinner={refreshing}
                            onClick={() => loadApps({ syncApsalarApps: true })}
                        >
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.REFRESH_APPS" />
                        </Button>
                    ) : null}
                    {!readOnly ? (
                        <Button type="primary" disabled={apps.some(app => app.id === NEW_APP_ID)} onClick={addNewApp}>
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.ADD_NEW_APP" />
                        </Button>
                    ) : null}
                </div>
            </div>

            {showTabs ? (
                <div className={css.statusTabs}>
                    {[DEV_STATUS_IN_PRODUCTION, DEV_STATUS_IN_TESTING].map(status => (
                        <button
                            key={status}
                            type="button"
                            className={classNames(css.statusTab, {
                                [css.statusTabActive]: developmentStatus === status,
                            })}
                            onClick={() => setDevelopmentStatus(status)}
                        >
                            {getStatusLabel(status)} ({appCountByStatus[status] || 0})
                        </button>
                    ))}
                </div>
            ) : null}

            {showLiveOnlyToggle ? (
                <SwitchLine
                    checked={showStoreLiveOnly}
                    className={css.liveOnlyRow}
                    onChange={checked => {
                        setShowStoreLiveOnly(checked);
                        trackAppsEvent('toggle show live', { on: checked });
                    }}
                    label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.SHOW_STORE_LIVE_ONLY_PROMPT" />}
                />
            ) : null}

            {loading ? <Spinner expanded show /> : null}
            {!loading && error ? (
                <div className={css.errorState}>
                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.LOAD_ERROR" />
                </div>
            ) : null}
            {!loading && !error && refreshError ? (
                <div className={css.errorState}>
                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.LOAD_ERROR" />
                </div>
            ) : null}
            {!loading && !error && !visibleApps.length && !showAppsEmptyState ? (
                <div className={css.emptyState}>
                    <Translate
                        id={
                            apps.length === 0
                                ? 'STATIC.PAGES.APPS_CONFIGURATION.NO_APPS_ADDED'
                                : 'STATIC.PAGES.APPS_CONFIGURATION.NO_RESULTS'
                        }
                    />
                </div>
            ) : null}
            {!loading && !error && visibleApps.length ? (
                <div className={css.content}>
                    {visibleApps.map(app => (
                        <AppCard
                            key={app.id}
                            app={app}
                            adminMode={adminMode}
                            isStandardTier={isStandardTier}
                            readOnly={readOnly}
                            configuration={configuration}
                            appsPermissions={appsPermissions}
                            userData={userData}
                            expandedSites={expandedSites}
                            isSiteDirty={site => isSiteDirty(app.id, site)}
                            moveTargets={apps}
                            platformNames={availablePlatformNames}
                            platforms={platforms}
                            getSiteKey={getSiteKey}
                            translate={translate}
                            onToggleSite={toggleSite}
                            onPatchSite={patchSiteWithConfirmation}
                            onRefreshStoreDetails={refreshSiteStoreDetails}
                            onTestFcmIntegration={testFcmIntegration}
                            onJumpToDuplicateSite={jumpToDuplicateSite}
                            onRestoreDuplicateSite={restoreDuplicateSite}
                            onSaveSite={saveSite}
                            onCancelSite={cancelSite}
                            onDeleteSite={deleteSite}
                            onToggleSiteDevelopmentStatus={toggleSiteDevelopmentStatus}
                            onMoveSite={moveSite}
                            onAppNameChange={changeAppName}
                            onSaveAppName={saveAppName}
                            onDeleteApp={deleteApp}
                            onAddPlatform={addPlatform}
                            onTrackAppsEvent={trackAppsEvent}
                        />
                    ))}
                </div>
            ) : null}
            <ConfirmDialog confirmation={confirmation} onAccept={acceptConfirmation} onReject={closeConfirmation} />
            <FirstAppDialog
                prompt={firstAppPrompt}
                isUnifiedUser={isUnifiedUser}
                onAddPlatform={addFirstAppPlatform}
                onClose={closeFirstAppPrompt}
                onContinue={continueFirstAppFlow}
                onRedirect={redirectFirstAppStep}
            />
        </PageWrapper>
    );
}

export default withLocalize(AppsPage);
